import { useEffect, useState } from 'react'
import { format } from 'date-fns'
import {
  Header,
  HeaderTitle
} from '@jana-technology/platform-ui'
import FullWidthLayoutContainer from 'common/components/FullWidthLayoutContainer'

import { ISelectOption } from '$/types'
import {
  findLookupTablesFromBaseSettings,
  useGetBaseSettingsQuery
} from 'app/services/api/basesettings'
import { useGetActiveSystemScopeFilterProfileQuery } from 'app/services/api/filterprofiles'
import {
  useAddHMCAClassLocationTableExportJobMutation,
  useGetHMCAClassLocationTableQuery
} from 'app/services/api/hmca/classlocation'
import {
  IGetHMCATableQueryParams,
  useAddHMCAConsequenceAreaTableExportJobMutation,
  useGetHMCAConsequenceAreaTableQuery
} from 'app/services/api/hmca/consequencearea'

import { HMCATableViewMode } from 'features/RegulatoryTools/HCAMCA/Components/Tables'
import HCAMCATableView from 'features/RegulatoryTools/HCAMCA/Components/Tables/Layout/HCAMCAView'
import TabsAndModals from 'features/RegulatoryTools/HCAMCA/Components/TabsAndModals'

import { skipQueryConditions } from './helpers'
import MapWrapperGuardMemo from './MapWrapperGuardMemo'

const HCAMCAPage = () => {
  const { systemScopeFilterProfile } =
    useGetActiveSystemScopeFilterProfileQuery()

  const { lookupTables } = useGetBaseSettingsQuery(undefined, {
    selectFromResult: ({ data }) => ({
      lookupTables: findLookupTablesFromBaseSettings(data)
    })
  })

  const [tableMode, setTableMode] = useState<HMCATableViewMode>(
    HMCATableViewMode.HMCA
  )

  // Remove unused options, rename remaining one to dateRange
  const [assessmentDateRange, setAssessmentDateRange] = useState<
    Array<Date | null>
  >([null, null])

  const [lineOptions, setLineOptions] = useState<ISelectOption[]>([])
  const [selectedLines, setSelectedLines] = useState<ISelectOption[]>([])

  const [selectedClassification, setSelectedClassification] = useState<
    ISelectOption[]
  >([])

  const [consequenceOptions, setConsequenceOptions] = useState<ISelectOption[]>(
    []
  )

  const [classLocationOptions, setClassLocationOptions] = useState<
    ISelectOption[]
  >([])

  const isValidDate = (d: Date | null) =>
    d instanceof Date && !isNaN(d as unknown as number)

  const hmcaTableQueryParams: IGetHMCATableQueryParams = {
    systemScopeId: systemScopeFilterProfile?.id?.toString() ?? '',
    routeIds:
      selectedLines.length > 0
        ? selectedLines.map((i: ISelectOption) => i.value as string)
        : (systemScopeFilterProfile?.continuousRoutes?.map(
            (line) => line.id as unknown as string
          ) ?? []),
    classification:
      selectedClassification?.map((i: ISelectOption) => i.value as string) ??
      [],
    fromDate: isValidDate(assessmentDateRange[0])
      ? format(assessmentDateRange[0]!, 'yyyy-MM-dd')
      : undefined,
    toDate: isValidDate(assessmentDateRange[1])
      ? format(assessmentDateRange[1]!, 'yyyy-MM-dd')
      : undefined
  }

  const { currentData: hmcaData } = useGetHMCAConsequenceAreaTableQuery(
    hmcaTableQueryParams,
    {
      skip: skipQueryConditions(
        hmcaTableQueryParams,
        systemScopeFilterProfile?.id == null || tableMode === 1
      )
    }
  )

  const { currentData: classLocationData } = useGetHMCAClassLocationTableQuery(
    hmcaTableQueryParams,
    {
      skip: skipQueryConditions(
        hmcaTableQueryParams,
        systemScopeFilterProfile?.id == null || tableMode === 0
      )
    }
  )

  // reset selected lines filter when system scope changes
  useEffect(() => {
    setSelectedLines([])
  }, [systemScopeFilterProfile])

  const [addHMCATableExportJob] =
    useAddHMCAConsequenceAreaTableExportJobMutation()

  const [addClassLocationTableExportJob] =
    useAddHMCAClassLocationTableExportJobMutation()

  const mutationTrigger =
    tableMode === HMCATableViewMode.HMCA
      ? addHMCATableExportJob
      : addClassLocationTableExportJob

  useEffect(() => {
    const tempLineOptions: ISelectOption[] = []

    systemScopeFilterProfile?.continuousRoutes?.forEach((line) => {
      tempLineOptions.push({ label: line.name, value: line.id })
    })
    setLineOptions(tempLineOptions)

    lookupTables?.details.forEach((table) => {
      if (table.key === 'ClassLocationType') {
        const classLocations = table.value as unknown as Array<{
          Code: string
          Name: string
        }>

        const classLocationOptions = classLocations.map((option) => {
          return { value: option.Code, label: option.Name }
        })

        setClassLocationOptions(classLocationOptions)
      } else if (table.key === 'ConsequenceAreaType') {
        const consequenceLevels = table.value as unknown as Array<{
          Code: string
          Name: string
        }>

        const consequenceLevelOptions = consequenceLevels.map((option) => {
          return { value: option.Code, label: option.Code }
        })

        setConsequenceOptions(consequenceLevelOptions)
      }
    })
  }, [systemScopeFilterProfile, lookupTables?.details])

  return (
    <div className='flex flex-1 flex-col overflow-auto'>
      <Header>
        <FullWidthLayoutContainer>
          <HeaderTitle>Consequence Area & Class Location</HeaderTitle>
        </FullWidthLayoutContainer>
      </Header>
      <TabsAndModals
        tableMode={tableMode}
        setTableMode={setTableMode}
        mutationTrigger={mutationTrigger}
        exportParams={hmcaTableQueryParams}
      />
      <div className='flex h-full'>
        <div className='w-2/3'>
          <HCAMCATableView
            tableData={
              tableMode === HMCATableViewMode.HMCA
                ? hmcaData
                : classLocationData
            }
            selectedLines={selectedLines}
            assessmentDateRange={assessmentDateRange}
            selectedClassification={selectedClassification}
            setSelectedLines={setSelectedLines}
            setAssessmentDateRange={setAssessmentDateRange}
            setSelectedClassification={setSelectedClassification}
            lineOptions={lineOptions}
            classificationOptions={
              tableMode === HMCATableViewMode.HMCA
                ? consequenceOptions
                : classLocationOptions
            }
          />
        </div>
        <div className='w-1/3'>
          <MapWrapperGuardMemo
            tableMode={tableMode}
            hmcaDataId={hmcaData?.mapAttributeId}
            classLocationDataId={classLocationData?.mapAttributeId}
            selectedDateRange={assessmentDateRange}
            selectedLines={selectedLines}
            selectedClassifications={selectedClassification}
          />
        </div>
      </div>
    </div>
  )
}

export default HCAMCAPage
